import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/Form';
import Message from 'components/Message';
import { schedulePropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenueId } from 'selectors/general';

import SegmentedCapacityBar, { SEGMENTED_CAPACITY_BAR_SEGMENT } from '../Components/SegmentedCapacityBar';
import { SCHEDULE_LIST_V2_FILL_BAR_VARIANT, getScheduleListV2FillBar } from '../ScheduleListV2/ScheduleListV2.helpers';
import { getMostRecentFillHistoryEntry } from './ScheduleDetailV2.helpers';
import { useScheduleInsights } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';
import './AllBookingsSection.scss';

const { block, element } = bem('all-bookings-section');

const AllBookingsSection = ({ schedule }) => {
  const navigate = useNavigate();
  const venueId = useSelector(selectActiveVenueId);
  const { data: insights } = useScheduleInsights(schedule.scheduleId);
  const fillBar = getScheduleListV2FillBar(schedule);
  const isIntegrated = fillBar.variant === SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED;
  const unfilledCount = fillBar.segments.find(({ key }) => key === SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED)?.value ?? 0;
  const lastWeekFill = getMostRecentFillHistoryEntry(insights?.history, schedule.startDateTime);

  const legendCount = count => <span className={element('legend-count')}>{count}</span>;
  const legendItems = [
    isIntegrated && {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.DIRECT,
      label: <Message message={messages.legendDirect} values={{ count: legendCount(fillBar.directBooked) }} />,
    },
    {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.CLASSPASS,
      label: <Message message={messages.legendClasspass} values={{ count: legendCount(fillBar.classpassBooked) }} />,
    },
    {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.UNFILLED,
      label: <Message message={messages.legendOpenSpots} values={{ count: legendCount(unfilledCount) }} />,
    },
  ].filter(Boolean);

  return (
    <section className={block()} data-cypress="schedule-detail-v2-all-bookings">
      <div className={element('title-row')}>
        <Message
          as="h3"
          className={element('title')}
          message={isIntegrated ? messages.allBookingsTitleIntegrated : messages.allBookingsTitleManual}
        />
        {lastWeekFill && (
          <Message
            as="span"
            className={element('caption')}
            data-cypress="schedule-detail-v2-last-week-fill"
            message={messages.lastWeekFill}
            values={{
              capacity: lastWeekFill.maxCapacity,
              filled: lastWeekFill.totalReservations,
              value: chunks => <span className={element('caption-value')}>{chunks}</span>,
            }}
          />
        )}
      </div>
      <SegmentedCapacityBar
        legendAction={
          <Button
            className={element('view-insights')}
            styled="inline-link"
            onClick={() => navigate(makePath.classInsights({ venueId }))}>
            <Message message={messages.viewMoreInsights} />
          </Button>
        }
        legendItems={legendItems}
        muted={schedule.isInThePast}
        segments={fillBar.segments}
      />
    </section>
  );
};

AllBookingsSection.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes),
};

export default AllBookingsSection;
